import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { Star, Heart, Loader2, CheckCircle } from "lucide-react";
import { toast } from "sonner";
import { supabase } from "../lib/supabase";

function ConfirmPopup({ open, message, onConfirm, onCancel }) {
  if (!open) return null;
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-black/50" onClick={onCancel} />
      <div className="relative bg-white rounded-3xl p-6 max-w-sm w-full shadow-2xl">
        <h3 className="title-pop text-xl mb-3" style={{ color: "var(--ff-text)" }}>Confirmar</h3>
        <p className="font-body mb-6" style={{ color: "var(--ff-text-soft)" }}>{message}</p>
        <div className="flex gap-3">
          <button onClick={onCancel} className="flex-1 px-4 py-3 rounded-full border border-slate-300 font-medium text-slate-600 hover:bg-slate-100 transition-colors">
            Cancelar
          </button>
          <button onClick={onConfirm} className="flex-1 px-4 py-3 rounded-full font-medium text-white transition-colors" style={{ background: "var(--ff-peach)" }}>
            Confirmar
          </button>
        </div>
      </div>
    </div>
  );
}

export default function Review() {
  const { token } = useParams();
  const [info, setInfo] = useState(null);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(true);
  const [rating, setRating] = useState(0);
  const [hover, setHover] = useState(0);
  const [comment, setComment] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [pendingSubmit, setPendingSubmit] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const { data, error: fetchError } = await supabase
          .from("reservations")
          .select("*")
          .eq("validation_token", token)
          .single();
        
        if (fetchError || !data) {
          setError("Link inválido ou expirado");
          return;
        }

        if (data.validated_at) {
          setInfo(data);
          setError("Esta reserva já foi validada anteriormente");
          return;
        }

        setInfo(data);
      } catch (e) { 
        setError("Link inválido ou expirado"); 
      } finally { 
        setLoading(false); 
      }
    })();
  }, [token]);

  const submit = async (e) => {
    e.preventDefault();
    if (!rating) return toast.error("Escolha uma classificação");
    setPendingSubmit(true);
    setConfirmOpen(true);
  };

  const handleConfirmSubmit = async () => {
    setConfirmOpen(false);
    setSubmitting(true);
    try {
      const { error: updateError } = await supabase
        .from("reservations")
        .update({
          validated_at: new Date().toISOString(),
          validation_rating: rating,
          validation_comment: comment || null,
        })
        .eq("validation_token", token);

      if (updateError) throw updateError;

      await supabase.from("reviews").insert({
        customer_name: info.name,
        item_name: info.item_name,
        event_date: info.event_date,
        rating: rating,
        comment: comment || null,
        published: false,
      });

      setDone(true);
    } catch (e) { 
      console.error(e);
      toast.error(e?.message || "Erro ao submeter"); 
    } finally { 
      setSubmitting(false); 
    }
  };

  const handleCancelSubmit = () => {
    setConfirmOpen(false);
    setPendingSubmit(false);
  };

  const deleteReview = async () => {
    setConfirmOpen(true);
    setPendingSubmit("delete");
  };

  const handleConfirmDelete = async () => {
    setConfirmOpen(false);
    setSubmitting(true);
    try {
      await supabase.from("reviews").delete().eq("customer_name", info.name).eq("item_name", info.item_name).eq("event_date", info.event_date);
      await supabase.from("reservations").update({ validated_at: null, validation_rating: null, validation_comment: null }).eq("validation_token", token);
      setError("");
      setInfo({ ...info, validated_at: null });
      toast.success("Avaliação eliminada");
    } catch (e) { 
      console.error(e);
      toast.error(e?.message || "Erro ao eliminar"); 
    } finally { 
      setSubmitting(false); 
    }
  };

  return (
    <>
      <ConfirmPopup 
        open={confirmOpen} 
        message={pendingSubmit === "delete" ? "Tem a certeza que deseja eliminar a sua avaliação?" : "Tem a certeza que deseja submeter a sua avaliação?"} 
        onConfirm={pendingSubmit === "delete" ? handleConfirmDelete : handleConfirmSubmit} 
        onCancel={handleCancelSubmit} 
      />
      {loading && <div className="min-h-screen flex items-center justify-center bg-cream"><Loader2 className="animate-spin" size={32} /></div>}
      {error && !loading && (
        <div className="min-h-screen flex items-center justify-center bg-cream p-5">
          <div className="max-w-md w-full text-center frame-scallop">
            <div className="frame-inner p-8">
              <h1 className="title-pop text-3xl mb-3" style={{ color: "var(--ff-text)" }}>Ups!</h1>
              <p className="font-body mb-4" style={{ color: "var(--ff-text-soft)" }}>{error}</p>
              {info?.validated_at && (
                <button onClick={deleteReview} className="text-sm px-4 py-2 rounded-full border border-red-300 text-red-600 hover:bg-red-50 font-medium transition-colors">
                  Eliminar avaliação
                </button>
              )}
            </div>
          </div>
        </div>
      )}
      {done && !loading && (
        <div className="min-h-screen flex items-center justify-center bg-cream p-5 relative overflow-hidden">
          <div className="absolute inset-0 bg-stripes-blue-soft opacity-40" />
          <div className="relative max-w-md w-full text-center frame-scallop">
            <div className="frame-inner p-10">
              <div className="w-20 h-20 mx-auto rounded-full flex items-center justify-center mb-5" style={{ background: "var(--ff-peach)" }}>
                <Heart className="text-white" fill="white" size={36} />
              </div>
              <h1 className="title-pop text-3xl md:text-4xl mb-3" style={{ color: "var(--ff-text)" }}>Obrigado!</h1>
              <p className="font-body" style={{ color: "var(--ff-text-soft)" }}>A sua avaliação foi enviada. Significa muito para nós!</p>
            </div>
          </div>
        </div>
      )}
      {!loading && !error && !done && (
        <div className="min-h-screen bg-cream p-5 py-12 relative overflow-hidden">
          <div className="absolute inset-0 bg-stripes-blue-soft opacity-30" />
          <div className="relative max-w-2xl mx-auto">
            <div className="text-center mb-8">
              <span className="title-balloon text-5xl block leading-none" style={{ color: "var(--ff-text)" }}>Fantastic</span>
              <span className="title-balloon text-4xl block leading-none -mt-2" style={{ color: "var(--ff-peach)" }}>Fun</span>
            </div>
            <div className="frame-scallop">
              <div className="frame-inner p-6 md:p-10">
                <h1 className="title-pop text-3xl md:text-4xl text-center mb-3" style={{ color: "var(--ff-text)" }}>Como foi a festa?</h1>
                <p className="text-center font-body mb-6" style={{ color: "var(--ff-text-soft)" }}>
                  Olá <strong>{info?.customer_name}</strong>! Conte-nos como correu o(a) <strong>{info?.item_name}</strong> no dia {info?.event_date}.
                </p>
                <form onSubmit={submit} className="space-y-6">
                  <div className="flex justify-center gap-2">
                    {[1,2,3,4,5].map((n) => (
                      <button key={n} type="button" onMouseEnter={() => setHover(n)} onMouseLeave={() => setHover(0)} onClick={() => setRating(n)} className="star-btn p-1">
                        <Star size={44} strokeWidth={1.5} fill={(hover||rating)>=n?"var(--ff-peach)":"transparent"} color={(hover||rating)>=n?"var(--ff-peach)":"#cbd5e1"} />
                      </button>
                    ))}
                  </div>
                  <div><label className="ff-label">Comentário</label>
                    <textarea className="ff-input min-h-[120px]" placeholder="Como foi a experiência?" value={comment} onChange={(e) => setComment(e.target.value)} data-testid="review-comment" /></div>
                  <button type="submit" disabled={submitting} className="ff-btn ff-btn-primary w-full" data-testid="review-submit">
                    {submitting ? <Loader2 className="animate-spin mr-2" size={18} /> : null}Enviar Avaliação
                  </button>
                </form>
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
